'use client';

import { format } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import { ctfImage } from '@/lib/contentful-url';
import { Markdown } from '@/lib/markdown';
import { trackClarityEvent } from '@/lib/microsoft-clarity';
import { newsHref } from '@/lib/routes';
import { INews } from '@/models/news';

/** News index — the destination of the section's "View all" link. */
const NEWS_INDEX_HREF = '/news';

/** Articles shown per row in the design; anything beyond this is dropped. */
const MAX_ARTICLES = 3;

interface IRelatedNews {
	className?: string;
	title?: string;
	articles: INews[];
	viewAllHref?: string;
}

/**
 * Related news for article and Solution pages, using the same restyled card as
 * [RelatedSolutions] (LIND-57). The two sections stay separate components
 * because they link, track and label differently; keep the card markup in step.
 */
export default function RelatedNews(props: IRelatedNews) {
	const articles = (props.articles ?? []).filter((article) => !!article?.slug);

	if (!articles.length) {
		return null;
	}

	return (
		<section className={`container ${props.className ?? ''}`}>
			<div className="mb-8 md:mb-12 flex items-baseline justify-between gap-6">
				<h2 className="text-2xl md:text-3xl font-bold text-primary-600">
					{props.title ?? 'Related news'}
				</h2>

				<Link
					prefetch={false}
					href={props.viewAllHref ?? NEWS_INDEX_HREF}
					onClick={() => trackClarityEvent('related_news_view_all_click')}
					className="flex-shrink-0 text-sm font-semibold text-primary-500 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
				>
					View all
				</Link>
			</div>

			<div className="grid gap-6 md:gap-8 grid-cols-1 md:grid-cols-3 items-stretch">
				{articles.slice(0, MAX_ARTICLES).map((article) => (
					<Link
						key={article.slug}
						prefetch={false}
						href={newsHref(article)}
						onClick={() =>
							trackClarityEvent('related_news_card_click', {
								related_article: article.slug ?? '',
							})
						}
						className="group flex h-full flex-col focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
					>
						{article.coverImage?.url ? (
							<div className="aspect-video w-full flex-shrink-0 overflow-hidden">
								<Image
									src={ctfImage(article.coverImage.url, {
										width: 800,
										quality: 75,
									})}
									alt={article.coverImage.title ?? article.title}
									width={800}
									height={450}
									sizes="(min-width: 768px) 33vw, 100vw"
									className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
								/>
							</div>
						) : null}

						{article.date ? (
							<p className="px-2 mt-4 text-sm font-semibold text-primary-500">
								{format(new Date(article.date), 'MMMM do, yyyy')}
							</p>
						) : null}

						<h3 className="px-2 mt-2 md:text-2xl font-bold line-clamp-2 text-[#0A0A0A]">
							{article.title}
						</h3>

						{article.excerpt ? (
							<div className="px-2 flex-1 text-gray-600">
								<Markdown
									content={article.excerpt}
									className="[&_p]:line-clamp-3 [&_p]:mb-0 [&_p]:text-sm [&_p]:md:text-base"
								/>
							</div>
						) : null}

						<span className="px-2 mt-4 text-sm font-semibold text-primary-500 group-hover:underline">
							Read more
						</span>
					</Link>
				))}
			</div>
		</section>
	);
}
